import {
  FieldPath,
  FieldValues,
  PathValue,
  UseFormReturn,
  useWatch,
} from "react-hook-form";
import { FormItemCommonProps, FormItemContent } from "./common";
import {
  DebouncedInput,
  DebouncedInputProps,
  DebouncedTextArea,
  DebouncedTextAreaProps,
  InferDebouncedInput,
} from "../debounced-input";
import { FormField, useFormField } from "@/components/ui/form";
import { callAll, cn } from "@/lib/utils";
import React from "react";
import { CopyButton } from "../copy";
import { ButtonProps } from "../button";

export type FormInputItemProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
> = {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  name: TName;
} & FormItemCommonProps &
  Omit<DebouncedInputProps, "onChange" | "form" | "value"> &
  Partial<Pick<DebouncedInputProps, "onChange" | "onBlur">> &
  InferDebouncedInput<PathValue<TFieldValues, TName>> & {
    fit?: boolean;
    fitProps?: React.HTMLProps<HTMLDivElement>;
  };

export type FormInputItemCommonProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
> = Omit<
  FormInputItemProps<TFieldValues, TName, TContext, TTransformedValues>,
  "form" | "name" | "label" | "type" | "isUndefinable" | "isNullable"
> &
  Partial<Pick<FormItemCommonProps, "label">> & { fit?: boolean };

export const FormDebouncedInput = React.forwardRef<
  HTMLInputElement,
  DebouncedInputProps
>(({ className, ...props }, ref) => {
  const { error } = useFormField();
  return (
    <DebouncedInput
      ref={ref}
      className={cn(error && "border-destructive", className)}
      {...props}
    />
  );
});

export function FormInputItem<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
>({
  form,
  name,
  label,
  container,
  onChange,
  onBlur,
  fit,
  fitProps,
  ...props
}: FormInputItemProps<TFieldValues, TName, TContext, TTransformedValues>) {
  // not `field.value`: useController shows the value the field mounted with whenever the value is
  // undefined, so an undefinable input emptied after being prefilled would bring the prefill back
  const value = useWatch({ control: form.control, name });
  const Wrap = React.useCallback(
    ({ children }) =>
      fit ? (
        <div
          {...fitProps}
          className={cn("inline-grid max-w-full", fitProps?.className)}
        >
          {children}
        </div>
      ) : (
        children
      ),
    [fit],
  );
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        return (
          <FormItemContent
            {...container}
            clear={
              (props as any).type === "checkbox" &&
              container?.clear === undefined
                ? null
                : container?.clear
            }
            label={label}
            className={cn(fit && "relative max-w-full", container?.className)}
            // inline={container?.inline ?? inline}
          >
            <Wrap>
              <FormDebouncedInput
                {...(field as any)}
                value={value}
                {...props}
                onChange={callAll(field.onChange, onChange)}
                onBlur={callAll(field.onBlur, onBlur)}
                className={cn(
                  fit && "w-full col-start-1 row-start-1",
                  props.className,
                )}
              />
              {fit && (
                <span className="px-3 w-fit max-w-full invisible col-start-1 row-start-1">
                  {/* falls back to the placeholder so an empty field is still wide enough to read it */}
                  {(props as any).value || value || (props as any).placeholder}
                </span>
              )}
            </Wrap>
          </FormItemContent>
        );
      }}
    />
  );
}

export type FormTextAreaItemProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
> = {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  name: TName;
} & FormItemCommonProps &
  Omit<DebouncedTextAreaProps, "value" | "onChange" | "form"> &
  Partial<Pick<DebouncedTextAreaProps, "onChange" | "onBlur">> & {
    copy?: boolean;
    copyWrapProps?: React.HTMLProps<HTMLDivElement>;
    copyProps?: ButtonProps;
  };

export type FormTextAreaItemCommonProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
> = Omit<
  FormTextAreaItemProps<TFieldValues, TName, TContext, TTransformedValues>,
  "form" | "name" | "label" | "type" | "isUndefinable" | "isNullable"
> &
  Partial<Pick<FormItemCommonProps, "label">> & { copy?: boolean };

export const FormDebouncedTextArea = React.forwardRef<
  HTMLTextAreaElement,
  DebouncedTextAreaProps
>(({ className, ...props }, ref) => {
  const { error } = useFormField();
  return (
    <DebouncedTextArea
      ref={ref}
      className={cn(error && "border-destructive", className)}
      {...props}
    />
  );
});

export function FormTextAreaItem<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
>({
  form,
  name,
  label,
  container,
  onChange,
  onBlur,
  copy,
  copyWrapProps,
  copyProps,
  ...props
}: FormTextAreaItemProps<TFieldValues, TName, TContext, TTransformedValues>) {
  // not `field.value`, see FormInputItem
  const value = useWatch({ control: form.control, name });

  const Wrap = React.useCallback(
    ({ children }) =>
      copy ? (
        <div
          {...copyWrapProps}
          className={cn("relative", copyWrapProps?.className)}
        >
          {children}
        </div>
      ) : (
        children
      ),
    [copy],
  );

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItemContent
          {...container}
          clear={
            (props as any).type === "checkbox" && container?.clear === undefined
              ? null
              : container?.clear
          }
          label={label}
          // inline={container?.inline ?? inline}
        >
          <Wrap>
            <FormDebouncedTextArea
              {...(field as any)}
              value={value}
              {...props}
              onChange={callAll(field.onChange, onChange)}
              onBlur={callAll(field.onBlur, onBlur)}
            />
            {copy && value && (
              <CopyButton
                {...copyProps}
                value={value || ""}
                className={cn(
                  "absolute top-2 right-2 z-[1]",
                  copyProps?.className,
                )}
              />
            )}
          </Wrap>
        </FormItemContent>
      )}
    />
  );
}
